import React, { useMemo } from 'react'
import {
  Checkbox,
  Modal,
  Select,
  type SelectOption,
} from '@enamelai/design-system'
import { Provider } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import type {
  ClearPlanProvidersModal,
  PendingBulkProvider,
  PlanProviderAssignModal,
} from './TreatmentPlanMain.types'

const procedureLabel = (count: number): string =>
  count === 1 ? 'procedure' : 'procedures'

export type TreatmentPlanProviderModalsProps = {
  planProviderAssignModal: PlanProviderAssignModal | null
  menuFillProviderId: string
  onMenuFillProviderIdChange: (value: string) => void
  onClosePlanProviderAssign: () => void
  onConfirmPlanProviderAssign: () => void
  clearPlanProvidersModal: ClearPlanProvidersModal | null
  onCloseClearPlanProviders: () => void
  onConfirmClearPlanProviders: () => void
  providerList: Provider[]
  pendingBulkProvider: PendingBulkProvider | null
  bulkProviderDontAskAgain: boolean
  onBulkProviderDontAskAgainChange: (checked: boolean) => void
  onBulkProviderModalHide: () => void
  onBulkProviderOnlyThisProcedure: () => void
  onConfirmBulkProviderApplyAll: () => void
}

const TreatmentPlanProviderModals: React.FC<
  TreatmentPlanProviderModalsProps
> = ({
  planProviderAssignModal,
  menuFillProviderId,
  onMenuFillProviderIdChange,
  onClosePlanProviderAssign,
  onConfirmPlanProviderAssign,
  clearPlanProvidersModal,
  onCloseClearPlanProviders,
  onConfirmClearPlanProviders,
  providerList,
  pendingBulkProvider,
  bulkProviderDontAskAgain,
  onBulkProviderDontAskAgainChange,
  onBulkProviderModalHide,
  onBulkProviderOnlyThisProcedure,
  onConfirmBulkProviderApplyAll,
}): React.ReactElement => {
  const assignCount = planProviderAssignModal?.affectedCount ?? 0
  const isReplace = planProviderAssignModal?.mode === 'replace'
  const assignedCount = clearPlanProvidersModal?.assignedCount ?? 0
  const bulkEmptyCount = pendingBulkProvider?.emptyCount ?? 0
  const providerOptions = useMemo((): SelectOption[] => {
    return providerList.map((option) => ({
      value: option.provider_id,
      label: `Dr. ${getFullName(option)}`,
    }))
  }, [providerList])

  return (
    <>
      <Modal
        open={!!planProviderAssignModal}
        onClose={onClosePlanProviderAssign}
        size="md"
        title={
          isReplace
            ? 'Replace providers on this plan'
            : 'Assign provider to unassigned procedures'
        }
        primaryLabel={isReplace ? 'Replace all' : 'Assign to all unassigned'}
        secondaryLabel="Cancel"
        primaryDisabled={menuFillProviderId.trim() === ''}
        onPrimaryClick={onConfirmPlanProviderAssign}
        onSecondaryClick={onClosePlanProviderAssign}
      >
        <p>
          {isReplace
            ? `Select a provider to apply to ${assignCount} ${procedureLabel(assignCount)} in this plan. Existing provider assignments will be replaced.`
            : `Select a provider to apply to ${assignCount} ${procedureLabel(assignCount)} in this plan that do not have a provider yet.`}
        </p>
        <Select
          label="Provider"
          placeholder="Select provider"
          options={providerOptions}
          value={menuFillProviderId}
          onChange={(value): void => {
            if (typeof value === 'string') {
              onMenuFillProviderIdChange(value)
            }
          }}
        />
      </Modal>
      <Modal
        open={!!clearPlanProvidersModal}
        onClose={onCloseClearPlanProviders}
        variant="confirmation"
        size="md"
        title="Clear all providers"
        description={`Remove the provider from ${assignedCount} ${procedureLabel(assignedCount)} in this plan. Each procedure needs a provider before this plan can be saved.`}
        primaryLabel="Clear providers"
        secondaryLabel="Cancel"
        onPrimaryClick={onConfirmClearPlanProviders}
        onSecondaryClick={onCloseClearPlanProviders}
      />
      <Modal
        className="plan-add-provider-modal"
        open={!!pendingBulkProvider}
        onClose={onBulkProviderModalHide}
        size="md"
        title="Apply provider to remaining procedures?"
        primaryLabel="Apply to remaining procedures"
        secondaryLabel="Only this procedure"
        onPrimaryClick={onConfirmBulkProviderApplyAll}
        onSecondaryClick={onBulkProviderOnlyThisProcedure}
      >
        <p>
          Apply Dr. {pendingBulkProvider?.providerDisplayName} to{' '}
          {bulkEmptyCount} other procedure{bulkEmptyCount === 1 ? '' : 's'} in
          this plan that do not have a provider yet?
        </p>
        <Checkbox
          id="tx-plan-bulk-provider-dont-ask"
          label="Don't ask again for this plan"
          checked={bulkProviderDontAskAgain}
          onChange={(event): void => {
            onBulkProviderDontAskAgainChange(event.target.checked)
          }}
        />
      </Modal>
    </>
  )
}

export default TreatmentPlanProviderModals
